import { Card, CardContent } from "../../ui/card"
import { SummaryHeader } from "../../ui/SummaryHeader"
import { useChartColors } from "../../../hooks/useChartColors"
import SalesLoader from "../../../components/loaders/SalesLoader"
import { useMemo, useState } from "react"
import { endOfMonth, format, startOfMonth } from "date-fns";
import { useEcommSales } from "../hooks/useSalesEcomm";

import { DataTable } from "../../ui/data-table/data-table"
import { EcommPerformanceFilter } from "./EcommPerformanceFilter";
import { Button } from "../../ui/button";
import { Filter, CalendarClock } from "lucide-react";
import { formatCurrency } from "../../../utils/formatCurrency"
import { formatNumber } from "../../../utils/formatNumber"
import { MonthYearPicker } from "../../ui/month-Year-Picker";


interface CompletePerformanceProps {
  selectedMonth: string;
}


interface CompletePerformanceMetrics {
  style: string;
  color: string;
  size: string;
  gender: string;
  category: string;
  collection: string;
  product_name: string;
  units_sold: number;
  average_price: number;
  revenue: number;
}

export function EcommCompletePerformance({ selectedMonth }: CompletePerformanceProps) {
  // Filter modal state
  const [filterOpen, setFilterOpen] = useState(false);
  // Filters: { field: string[] }
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  // Numeric ranges: { field: [min, max] }
  const [numericRanges, setNumericRanges] = useState<Record<string, [number?, number?]>>({});

  const now = new Date();
  const initialDate = selectedMonth ? (() => {
    const [year, month] = selectedMonth.split('-').map(Number);
    return new Date(year, month - 1, 1); // month - 1 because Date months are 0-indexed but yyyy-MM string is 1-indexed
  })() : new Date(now.getFullYear(), now.getMonth());
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(initialDate);
  
  const jumpToCurrentMonth = () => {
    const current = new Date();
    setSelectedDate(new Date(current.getFullYear(), current.getMonth()));
  };

  const { startDate, endDate } = useMemo(() => {
    const monthDate = selectedDate ?? new Date(now.getFullYear(), now.getMonth());
    return {
      startDate: format(startOfMonth(monthDate), "yyyy-MM-dd"),
      endDate: format(endOfMonth(monthDate), "yyyy-MM-dd"),
    };
  }, [selectedDate, now]);



  const { data, isLoading } = useEcommSales(startDate, endDate);


  // Raw data
  const shopifyData: CompletePerformanceMetrics[] = useMemo(() =>
    (data?.shopify_complete_performance || []).map((cat) => ({
      style: cat.style,
      color: cat.color,
      size: cat.size,
      gender: cat.gender,
      category: cat.category,
      collection: cat.collection,
      product_name: cat.product_name,
      units_sold: cat.units_sold,
      average_price: cat.average_price,
      revenue: cat.revenue,    
    }))
    , [data]);

  // Build filter options from data
  const filterOptions = useMemo(() => {
    const opts: Record<string, Set<string>> = {
      style: new Set(),
      color: new Set(),
      size: new Set(),
      gender: new Set(),
      category: new Set(),
      collection: new Set(),
      product_name: new Set(),
    };
    shopifyData.forEach(row => {
      Object.keys(opts).forEach(field => {
        if (row[field as keyof CompletePerformanceMetrics]) {
          opts[field].add(String(row[field as keyof CompletePerformanceMetrics]));
        }
      });
    });
    // Convert sets to sorted arrays
    return Object.fromEntries(
      Object.entries(opts).map(([k, v]) => [k, Array.from(v).sort()])
    );
  }, [shopifyData]);

  // Filter data based on filters and numeric ranges
  const filteredData = useMemo(() => {
    // If no filters at all, return all data
    const hasCategorical = filters && Object.keys(filters).some(f => (filters[f] || []).length > 0);
    const hasNumeric = Object.keys(numericRanges).some(f => {
      const [min, max] = numericRanges[f] || [];
      return min !== undefined || max !== undefined;
    });
    if (!hasCategorical && !hasNumeric) return shopifyData;
    return shopifyData.filter(row => {
      // Categorical filters
      const catOk = Object.entries(filters).every(([field, vals]) => {
        if (!vals || vals.length === 0) return true;
        return vals.includes(String(row[field as keyof CompletePerformanceMetrics]));
      });
      // Numeric range filters
      const numOk = ["average_price", "units_sold", "revenue"].every(field => {
        const [min, max] = numericRanges[field] || [];
        const value = row[field as keyof CompletePerformanceMetrics] as number;
        if (min !== undefined && value < min) return false;
        if (max !== undefined && value > max) return false;
        return true;
      });
      return catOk && numOk;
    });
  }, [shopifyData, filters, numericRanges]);

  const noData = filteredData.length === 0;


  const columns = [
  {
    accessorKey: "style",
    header: "SKU",
    cell: (info: any) => info.getValue(),
  },
  {
    accessorKey: "product_name",
    header: "Product Name",
    cell: (info: any) => info.getValue(),
  },
  {
    accessorKey: "collection",
    header: "Collection",
    cell: (info: any) => info.getValue(),
  },
  {
    accessorKey: "size",
    header: "Size",
    cell: (info: any) => info.getValue(),
  },
  {
    accessorKey: "color",
    header: "Color",
    cell: (info: any) => info.getValue(),
  },
  {
    accessorKey: "gender",
    header: "Gender",
    cell: (info: any) => info.getValue(),
  },
  {
    accessorKey: "category",
    header: "Category",
    cell: (info: any) => info.getValue(),
  },
  {
    accessorKey: "average_price",
    header: "Average Price",
    cell: (info: any) => formatCurrency(info.getValue()),
  },
  {
    accessorKey: "units_sold",
    header: "Units Sold",
    cell: (info: any) => formatNumber(info.getValue()),
  },
  {
    accessorKey: "revenue",
    header: "Revenue",
    cell: (info: any) => formatCurrency(info.getValue()),
  },
];


  const colors = useChartColors("ecommerce")
  if (!colors) return null;
  return (
    <>
      <SummaryHeader title="Performance Table" />
      <Card>
      <div className="flex items-center justify-between m-4">
        <Button variant="outline" size="sm" onClick={() => setFilterOpen(true)}>
          <Filter className="w-4 h-4 mr-1" /> Advance Filter
        </Button>
        <div className="flex items-center gap-2 pr-3">
          <Button variant="outline" size="sm" onClick={jumpToCurrentMonth}>
            <CalendarClock className="h-4 w-4 mr-1" />
            Current Month
          </Button>
          <MonthYearPicker selected={selectedDate} onChange={setSelectedDate} />
        </div>
       </div>
      <EcommPerformanceFilter
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={filters}
        setFilters={setFilters}
        options={filterOptions}
        numericRanges={numericRanges}
        setNumericRanges={setNumericRanges}
      />
      
        <CardContent>
          {isLoading ? (
            <SalesLoader variant="performance-channel" />
          ) : noData ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              No Data Available
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={filteredData}
              searchColumn="sku"
              searchPlaceholder="Search by SKU..."
              showRowsPerPage={false}
              initialPageSize={5}
              showTotal={true} // Enable totals row
              totalColumns={["revenue"]} // Columns to total
            />
          )}
        </CardContent>
      </Card>
    </>
  );
}

